import { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { resolveTranslation, interpolate } from './admin_translations';

const LanguageContext = createContext();

/** Domain-based default language — checked on every fresh page load.
 *  - `japantollreceipts.jp` (and any subdomain) → Japanese
 *  - URL prefix `/en/...` and `/ja/...` are the only language switches
 *    for the active product. The legacy `.com` alias is a redirect-only
 *    domain at the DNS layer and never reaches this code in production.
 *  - Anything else (preview, localhost, partner staging) → null, falls
 *    back to stored preference, then 'ja'. */
function _domainDefaultLang() {
  if (typeof window === 'undefined') return null;
  const host = (window.location.hostname || '').toLowerCase();
  if (host === 'japantollreceipts.jp' || host.endsWith('.japantollreceipts.jp')) return 'ja';
  return null;
}

function _initialLang() {
  // 1) URL prefix wins — if the page is being loaded directly on
  //    `/ja/...` or `/en/...`, initialise to that language so the
  //    first paint is correct. Without this, refreshing on `/en/pricing`
  //    on the `.jp` domain would flash Japanese before LangPathEnforcer
  //    syncs state back to English. Customer-reported regression: a
  //    visitor in English should never see Japanese pop in mid-browse
  //    just because the URL happens to be on the .jp domain.
  if (typeof window !== 'undefined') {
    const path = window.location.pathname || '';
    if (path === '/ja' || path.startsWith('/ja/')) return 'ja';
    if (path === '/en' || path.startsWith('/en/')) return 'en';
  }
  // 2) Manual toggle within this tab wins (survives refresh)
  try {
    const sess = sessionStorage.getItem('jtr_lang_session');
    if (sess === 'ja' || sess === 'en') return sess;
  } catch (_) {}
  // 3) Domain default — `japantollreceipts.jp` ALWAYS defaults to
  //    Japanese on a fresh page load, even if the visitor previously
  //    toggled to English in a different tab. This is the customer's
  //    explicit product rule: "Japanese has to be the default
  //    language" on the .jp domain. Within a session the toggle still
  //    works (sessionStorage + URL prefix above), but a refresh / new
  //    tab on .jp with no prefix must land in Japanese.
  const fromDomain = _domainDefaultLang();
  if (fromDomain) return fromDomain;
  // 4) Cross-tab manual preference — honored when the active host has
  //    no domain rule (preview / localhost / partner staging). On the
  //    `.jp` production host the domain rule above always wins.
  try {
    const stored = localStorage.getItem('jtr_lang');
    if (stored === 'ja' || stored === 'en') return stored;
  } catch (_) {}
  // 5) Final fallback — Japanese, since the majority of clients are
  //    Japanese and JTR is a Japan-host-country product.
  return 'ja';
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(_initialLang);

  // Apply <html lang> on initial mount + whenever language changes.
  // Also pulse a brief opacity transition on <body> so the text swap
  // feels deliberate (300ms fade-out → fade-in) instead of a hard
  // flicker. The first effect run (initial mount) skips the pulse so
  // the page doesn't fade in on its own load.
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.setAttribute('translate', 'no');
  }, [lang]);

  // Track whether this is the initial render — we only animate on
  // subsequent lang changes, never on first mount.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (!document.body.dataset.langHydrated) {
      document.body.dataset.langHydrated = '1';
      return undefined;
    }
    document.body.classList.add('jtr-lang-swapping');
    const t = setTimeout(() => {
      document.body.classList.remove('jtr-lang-swapping');
    }, 300);
    return () => clearTimeout(t);
  }, [lang]);

  const setLang = useCallback((newLang, opts) => {
    if (newLang !== 'ja' && newLang !== 'en') return;
    setLangState(newLang);
    // Persist for this tab only — domain rule takes over on a new tab
    try { sessionStorage.setItem('jtr_lang_session', newLang); } catch (_) {}
    // Keep legacy localStorage in sync for any older code that still reads it
    try { localStorage.setItem('jtr_lang', newLang); } catch (_) {}
    document.documentElement.lang = newLang;
    document.documentElement.setAttribute('translate', 'no');

    // Iter 330 — Opt-in server-side persistence. When the user
    // explicitly toggles JA/EN from a logged-in surface (topbar
    // toggle, settings page), pass `{ persist: true }` so we save
    // the choice to `user.preferred_language`. Without this, the
    // toggle only lasts for the current session — and on the next
    // refresh / new tab the user's saved preference reasserts
    // itself, which is genuinely confusing. We never auto-persist
    // for marketing-site path-detection toggles or for the post-
    // login auto-sync, since those reflect the saved value (not a
    // fresh user choice).
    if (opts && opts.persist) {
      // Fire-and-forget. The endpoint is auth-required; if the user
      // isn't logged in we just swallow the 401 silently — the local
      // toggle still works for the current tab.
      try {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customer/settings`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ preferred_language: newLang }),
        }).catch(() => { /* offline / not authed — fine */ });
      } catch (_) { /* noop */ }
    }
  }, []);

  const toggleLang = useCallback((opts) => {
    const newLang = lang === 'ja' ? 'en' : 'ja';
    setLang(newLang, opts);
  }, [lang, setLang]);

  // Sync language from user's saved preference (called after login)
  const syncFromUser = useCallback((userLang) => {
    if (userLang === 'ja' || userLang === 'en') {
      setLang(userLang);
    }
  }, [setLang]);

  // Translation helper: t('customers.filterAll') → "All customers" / "すべての顧客"
  // Supports {placeholder} interpolation via second arg: t('customers.countOfTotal', {n:5,total:20})
  const t = useCallback(
    (key, vars) => {
      const resolved = resolveTranslation(key, lang);
      return vars ? interpolate(resolved, vars) : resolved;
    },
    [lang]
  );

  const value = useMemo(
    () => ({ lang, setLang, toggleLang, syncFromUser, t }),
    [lang, setLang, toggleLang, syncFromUser, t]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}

export function detectLangFromPath(pathname) {
  if (pathname.startsWith('/ja')) return 'ja';
  if (pathname.startsWith('/en')) return 'en';
  return null;
}

/**
 * swapLangPrefix — the single, tested rule for keeping a public URL's locale
 * prefix in sync with an explicit language switch (locale continuity). Bare
 * (unprefixed) public paths get the prefix prepended; already-prefixed paths
 * get swapped; root canonicalises to `/{newLang}`. Query/hash are preserved by
 * the caller. This is what guarantees a /ja visitor toggling to English lands
 * on the /en version of the SAME page — never a silent fallback.
 */
export function swapLangPrefix(path, newLang) {
  if (!path || path === '/') return `/${newLang}`;
  if (path === '/ja' || path === '/en') return `/${newLang}`;
  if (path.startsWith('/ja/')) return path.replace('/ja/', `/${newLang}/`);
  if (path.startsWith('/en/')) return path.replace('/en/', `/${newLang}/`);
  return `/${newLang}${path}`;
}

/** Used by the language toggle button to know which domain a manual switch
 *  is overriding — useful for analytics or for an optional "swap to .com"
 *  affordance later. Public so other pages can decide UX. */
export function getDomainDefaultLang() {
  return _domainDefaultLang();
}
